import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export function Table({
  className,
  ...props
}: ComponentPropsWithoutRef<"table">) {
  return (
    <div className="studio-table-frame max-w-full overflow-auto rounded-lg bg-paper">
      <table
        className={cn(
          "studio-table w-full min-w-max border-separate border-spacing-0 text-start font-sans",
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function TableHeader({
  className,
  ...props
}: ComponentPropsWithoutRef<"thead">) {
  return <thead className={className} {...props} />;
}

export function TableBody({
  className,
  ...props
}: ComponentPropsWithoutRef<"tbody">) {
  return <tbody className={className} {...props} />;
}

export interface TableRowProps extends ComponentPropsWithoutRef<"tr"> {
  selected?: boolean;
  archived?: boolean;
}

export function TableRow({
  selected,
  archived,
  className,
  ...props
}: TableRowProps) {
  return (
    <tr
      className={cn(
        "min-h-11 hover:bg-canvas",
        selected && "[--table-row-bg:var(--selection-bg)]",
        archived && "text-ink-faint",
        className,
      )}
      {...props}
    />
  );
}

export interface TableHeadProps extends ComponentPropsWithoutRef<"th"> {
  sort?: "ascending" | "descending" | "none";
  onSort?: () => void;
  numeric?: boolean;
}

export function TableHead({
  sort,
  onSort,
  numeric,
  className,
  children,
  ...props
}: TableHeadProps) {
  const content = onSort ? (
    <button
      type="button"
      onClick={onSort}
      className={cn(
        "flex min-h-9 w-full items-center gap-1.5 outline-none hover:text-ink focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--focus-ring)]",
        numeric && "justify-end",
      )}
    >
      {numeric ? <SortMark sort={sort} /> : null}
      <span>{children}</span>
      {!numeric ? <SortMark sort={sort} /> : null}
    </button>
  ) : (
    children
  );
  return (
    <th
      scope="col"
      aria-sort={sort && sort !== "none" ? sort : undefined}
      className={cn(
        "h-10 bg-canvas-soft px-4 text-start text-[11.5px] font-semibold tracking-[0.03em] text-ink-soft",
        sort && sort !== "none" && "text-ink",
        numeric && "text-end",
        className,
      )}
      {...props}
    >
      {content}
    </th>
  );
}

function SortMark({
  sort = "none",
}: {
  sort?: "ascending" | "descending" | "none";
}) {
  return (
    <span
      aria-hidden="true"
      className={cn("text-xs", sort === "none" ? "text-ink-faint" : "text-ink")}
    >
      {sort === "ascending" ? "↑" : sort === "descending" ? "↓" : "↕"}
    </span>
  );
}

export function TableCell({
  numeric,
  className,
  ...props
}: ComponentPropsWithoutRef<"td"> & { numeric?: boolean }) {
  return (
    <td
      className={cn(
        "h-12 px-4 text-[13px] text-ink",
        numeric && "text-end tabular-nums",
        className,
      )}
      {...props}
    />
  );
}

export interface TableSelectionCellProps {
  label: string;
  checked: boolean;
  mixed?: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}

export function TableSelectionCell({
  label,
  checked,
  mixed,
  disabled,
  onChange,
}: TableSelectionCellProps) {
  return (
    <label className={cn("studio-table-selection grid size-11 items-center justify-items-start", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
      <span className="sr-only">{label}</span>
      <input
        type="checkbox"
        ref={(node) => { if (node) node.indeterminate = !!mixed; }}
        checked={checked}
        disabled={disabled}
        aria-checked={mixed ? "mixed" : checked}
        onChange={(event) => onChange(event.target.checked)}
        className="studio-checkbox cursor-pointer disabled:cursor-not-allowed"
      />
    </label>
  );
}

export function TableToolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mb-2 flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-md bg-canvas-soft px-4 py-3",
        className,
      )}
    >
      {children}
    </div>
  );
}
